import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useOrdersCache } from "@web-app/api";
import {
  AccordionV2,
  Button,
  ErrorMessage,
  Field,
  Header,
  InfoBox,
  Label,
  useAlert,
} from "@web-app/components";
import { Order, Route } from "@web-app/types";
import { getMetersToMiles } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

const NOTES_MAX = 2000;

const onfleetFormSchema = z.object({
  notes: z
    .string()
    .trim()
    .max(NOTES_MAX, `Notes cannot exceed ${NOTES_MAX} characters`),
});

type OnfleetFormData = z.infer<typeof onfleetFormSchema>;

export function OrderOnfleetDetails({
  onClose,
  entity,
  entityId,
  orders,
  route,
}: {
  entity: "order" | "route";
  entityId: string;
  onClose: () => void;
  orders: Order[];
  route?: Route;
}) {
  const { showAlertAsync } = useAlert();
  const { invalidateOrdersCache } = useOrdersCache();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const { control, handleSubmit } = useForm<OnfleetFormData>({
    defaultValues: {
      notes: "",
    },
    mode: "onChange",
    resolver: zodResolver(onfleetFormSchema),
  });

  const missingFields = getMissingAddressFields(orders);
  const hasValidationErrors = missingFields.length > 0;

  const handleSendToOnfleet = async (formData: OnfleetFormData) => {
    setIsLoading(true);
    setServerError(null);

    const payload = {
      notes: formData.notes,
    };

    await showAlertAsync(
      http.post(`/integrations/onfleet/${entity}/${entityId}/tasks`, payload),
      {
        error: (err: unknown) => {
          setIsLoading(false);
          if (err instanceof AxiosError) {
            const errMsg = `${err.response?.data?.message || err.response?.data?.error}`;
            setServerError(errMsg.trim());
          }
          return {
            message: "Failed to send to Onfleet",
            title: "Error",
            type: "error",
          };
        },
        loading: {
          message: "Sending to Onfleet...",
          title: "Sending",
          type: "default",
        },
        success: () => {
          invalidateOrdersCache();
          onClose();
          return {
            message: `${entity === "order" ? "Order" : "Route"} sent to Onfleet`,
            title: "Success",
            type: "success",
          };
        },
      },
    );
  };

  return (
    <div className="absolute bg-white h-full w-full z-30 flex flex-col">
      {/* Sticky Header */}
      <Header
        title={`Send ${entity === "order" ? "Order" : "Route"} to Onfleet`}
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={onClose}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto py-4">
        {hasValidationErrors && (
          <InfoBox
            className="mb-4 mx-4"
            message={`Missing required address fields: ${missingFields.join(", ")}. Please update the order address before sending to Onfleet.`}
          />
        )}

        {serverError && (
          <InfoBox
            className="mb-4 mx-4 border-red-300 bg-red-50"
            iconClassName="text-red-600"
            title="Error"
            message={serverError}
          />
        )}

        <div className="w-full bg-white rounded-md">
          <div className="flex flex-col gap-4">
            {/* Order summary */}
            <div className="flex flex-col">
              {entity === "route" ? (
                <div className="flex flex-col gap-2 px-4 pb-2 border-b border-gray-200">
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">
                      No. of Orders
                    </span>
                    <span className="tw-details-field-value">
                      {orders.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">
                      Total Distance
                    </span>
                    <span className="tw-details-field-value">
                      {route?.estimatedDistance
                        ? `${getMetersToMiles(route.estimatedDistance)} miles`
                        : "N/A"}
                    </span>
                  </div>
                </div>
              ) : (
                <Label className="text-sm font-semibold text-gray-900 px-4">
                  Order Details
                </Label>
              )}
              {entity === "route" ? (
                orders.map((order) => (
                  <AccordionV2 key={order.id} defaultOpen={false} isBorderless>
                    <AccordionV2.Label textAlign="left" labelClassName="pl-3">
                      {order.externalId || order.displayId}
                    </AccordionV2.Label>
                    <AccordionV2.Content>
                      <OrderDetailFields order={order} />
                    </AccordionV2.Content>
                  </AccordionV2>
                ))
              ) : (
                <OrderDetailFields order={orders[0]} />
              )}
            </div>

            {/* Notes field */}
            <Field className="w-full px-4">
              <Label>Notes (optional)</Label>
              <Controller
                name="notes"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <textarea
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Add notes for the driver..."
                      rows={3}
                      maxLength={NOTES_MAX}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {error && <ErrorMessage message={error.message} />}
                  </>
                )}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex gap-2">
          <Button
            type="button"
            isLoading={isLoading}
            disabled={hasValidationErrors}
            onClick={handleSubmit(handleSendToOnfleet)}
          >
            Send to Onfleet
          </Button>
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function OrderDetailFields({ order }: { order: Order }) {
  return (
    <div className="flex flex-col gap-2 py-3 [&_.tw-details-field-title]:pl-3">
      {order.pickup && (
        <div className="flex flex-col gap-2 [&:not(:last-child)]:border-b border-gray-200 pb-3">
          <div className="grid pb-1">
            <span className="tw-details-field-title font-semibold">
              Pickup Details
            </span>
          </div>
          {order.pickup.name && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Name</span>
              <span className="tw-details-field-value">
                {order.pickup.name}
              </span>
            </div>
          )}
          {order.pickup.phone && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Phone</span>
              <span className="tw-details-field-value">
                {order.pickup.phone}
              </span>
            </div>
          )}
          {order.pickup.address && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Address</span>
              <span className="tw-details-field-value break-words">
                {order.pickup.address}
              </span>
            </div>
          )}
        </div>
      )}

      {order.dropoff && (
        <div className="flex flex-col gap-2 [&:not(:last-child)]:border-b border-gray-200 pb-3">
          <div className="grid pb-1">
            <span className="tw-details-field-title font-semibold">
              Drop-off Details
            </span>
          </div>
          {order.dropoff.name && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Name</span>
              <span className="tw-details-field-value">
                {order.dropoff.name}
              </span>
            </div>
          )}
          {order.dropoff.phone && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Phone</span>
              <span className="tw-details-field-value">
                {order.dropoff.phone}
              </span>
            </div>
          )}
          {order.dropoff.address && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Address</span>
              <span className="tw-details-field-value break-words">
                {order.dropoff.address}
              </span>
            </div>
          )}
        </div>
      )}

      {order.scheduledStartDateTime && (
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Scheduled Start</span>
          <span className="tw-details-field-value">
            {new Date(order.scheduledStartDateTime).toLocaleString()}
          </span>
        </div>
      )}
      {order.scheduledEndDateTime && (
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Scheduled End</span>
          <span className="tw-details-field-value">
            {new Date(order.scheduledEndDateTime).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

function getMissingAddressFields(orders: Order[]): string[] {
  const missing: string[] = [];
  for (const order of orders) {
    const label =
      orders.length > 1 ? `${order.externalId || order.displayId} ` : "";
    if (order.pickup) {
      if (!order.pickup.locality) missing.push(`${label}pickup city`);
      if (!order.pickup.country) missing.push(`${label}pickup country`);
    }
    if (order.dropoff) {
      if (!order.dropoff.locality) missing.push(`${label}dropoff city`);
      if (!order.dropoff.country) missing.push(`${label}dropoff country`);
    }
  }
  return missing;
}
